import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid, Typography } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { Form } from 'react-final-form'
import { Link as RouterLink } from 'react-router-dom'
import { ValidationErrors } from 'final-form'
import { useDispatch } from 'react-redux'
import { setRequiredErrors } from '../../utils/ui'
import { AsyncButton } from '../../shared/components/asyncButton'
import { promiseError, logout, setUser } from '../../shared/redux/actions/common'
import { getJson, sendJson } from '../../shared/utils/api'
import { removeToken } from '../../config'
import { closeWebSocket } from '../../utils/currentSession'
import { CustomTextField } from '../../shared/components/customInputRff'
import IdCard from '../../static/icons/icon-id-card-blue.svg'
import IdCardRed from '../../static/icons/icon-id-card-red.svg'
import PasswordIcon from '../../static/icons/icon-password-blue.svg'
import PasswordRedIcon from '../../static/icons/icon-password-red.svg'

// povinne overenie telefonneho cisla prihlaseneho pouzivatela (novy google ucet) - rovnako ako v appke
// AppRouter sem presmeruje kazdu stranku, kym nie je cislo overene; jedina dalsia moznost je odhlasenie

const useStyles = makeStyles((theme) => ({
    wrapper: {
        height: 'auto',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'auto',
        backgroundColor: '#000507',
        fontFamily: "'Mulish', sans-serif",
    },
    wrapperBottom: {
        height: 'auto',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'auto',
        backgroundColor: '#000507',
        fontFamily: "'Mulish', sans-serif",
        backgroundImage: 'linear-gradient(to bottom, rgba(55, 192, 202, 0) 1%, rgba(55, 191, 201, 0.46))',
    },
    wrapperInner: {
        maxWidth: '400px',
        paddingBottom: '0px',
        padding: '23px',
        margin: 'auto',
        color: 'white',
    },
    title: {
        fontFamily: "'Play', sans-serif",
        fontSize: '32px',
        fontWeight: 'normal',
        lineHeight: 'normal',
        color: 'white',
        textAlign: 'center',
    },
    form: {
        width: '100%',
        zoom: '0.75',
    },
    formItem: {
        paddingBottom: '26px',
    },
    titleContainer: {
        marginBottom: '57px',
        textAlign: 'center',
    },
    buttonText: {
        fontSize: '14px',
        fontWeight: 'initial',
        letterSpacing: '1px',
        color: 'white',
        fontFamily: "'Mulish', sans-serif",
    },
    buttonContainer: {
        marginBottom: '19px',
        marginLeft: '37px',
        marginRight: '37px',
        marginTop: '30px',
    },
    secondButtonContainer: {
        marginBottom: '19px',
        marginLeft: '37px',
        marginRight: '37px',
    },
    button: {
        backgroundImage: 'linear-gradient(259deg, #268890 1%, #42c7d0 95%, #42c7d0 5%)',
        height: '60px',
        borderRadius: '14px',
    },
    link: {
        marginTop: '19px',
        marginBottom: '30px',
        textAlign: 'center',
        '& a': {
            color: '#37c0ca',
            textDecoration: 'none',
            '&:hover': {
                color: '#37c0ca',
                textDecoration: 'underline',
            },
        },
    },
    // nenapadne odhlasenie - nie je to hlavna akcia
    logoutLink: {
        marginBottom: '30px',
        textAlign: 'center',
        '& a': {
            color: '#757575',
            textDecoration: 'none',
            '&:hover': {
                color: '#9e9e9e',
            },
        },
    },
    inputIconLeft: {
        color: '#37c0ca',
        marginBottom: '14px',
        marginTop: '20px',
        marginLeft: '8px',
        height: '17px',
    },
}))

const RESEND_SECONDS = 60

const PhoneVerification = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const classes = useStyles()
    const history = useHistory()

    const [loaded, setLoaded] = useState<boolean>(false)
    const [inProgress, setInProgress] = useState<boolean>(false)
    const [phone, setPhone] = useState<string>('')
    const [sentToPhone, setSentToPhone] = useState<string>('')
    const [codeStep, setCodeStep] = useState<boolean>(false)
    const [resendCountdown, setResendCountdown] = useState<number>(0)

    useEffect(() => {
        if (resendCountdown <= 0) return
        const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
        return () => clearTimeout(timer)
    }, [resendCountdown])

    // po overeni nacitame pouzivatela znova - AppRouter potom pusti na ostatne stranky
    const finish = async () => {
        const user = await getJson('users/me')
        dispatch(setUser(user))
        history.replace('/')
    }

    useEffect(() => {
        getJson('users/phone')
            .then(async (status: any) => {
                if (status?.confirmed) {
                    // ulozeny pouzivatel bol neaktualny - cislo uz je overene
                    await finish()
                    return
                }
                // predvyplnime len ak to vyzera ako cislo (stare ucty maju v Phone aj text)
                const stored: string = status?.phone || ''
                setPhone(/(\d\D*){6,}/.test(stored) ? stored : '')
            })
            .catch(() => {})
            .finally(() => setLoaded(true))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const showPhoneError = (err: any) => {
        const code = err?.errors?.[0]?.code
        dispatch(promiseError(code ? t(`register.phoneErrors.${code}`) : t('common.unknownValidationFailure')))
    }

    const sendCode = async (value: string) => {
        const trimmed = (value || '').trim()
        if (!trimmed || inProgress) return
        setInProgress(true)
        try {
            const response: any = await sendJson('users/phone/sendCode', 'POST', { phone: trimmed })
            setPhone(trimmed)
            setSentToPhone(response?.phone ?? trimmed)
            setCodeStep(true)
            setResendCountdown(response?.resendAfterSeconds ?? RESEND_SECONDS)
        } catch (err: any) {
            const code = err?.errors?.[0]?.code
            // eslint-disable-next-line eqeqeq
            if (code == 4) {
                // cislo uz je overene
                await finish()
                return
            }
            showPhoneError(err)
        } finally {
            setInProgress(false)
        }
    }

    const verifyCode = async (code: string) => {
        if (inProgress) return
        setInProgress(true)
        try {
            await sendJson('users/phone/verifyCode', 'POST', { code: (code || '').trim() })
            await finish()
        } catch (err: any) {
            showPhoneError(err)
        } finally {
            setInProgress(false)
        }
    }

    const handleLogout = (e: React.MouseEvent) => {
        e.preventDefault()
        if (inProgress) return
        removeToken()
        closeWebSocket()
        dispatch(logout())
    }

    if (!loaded) return null

    const logoutLink = (
        <Grid item xs={12} className={classes.logoutLink}>
            <RouterLink to="#" onClick={handleLogout}>
                {t('common.logout')}
            </RouterLink>
        </Grid>
    )

    if (!codeStep) {
        return (
            <Form
                initialValues={{ phone }}
                validate={(model: any): ValidationErrors => {
                    const errors: any = {}
                    setRequiredErrors(errors, model, ['phone'], t('register.required'))
                    return errors
                }}
                onSubmit={(model: any) => sendCode(model.phone)}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form} autoComplete="off">
                        <div className={classes.wrapper}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.titleContainer}>
                                    <Typography variant="h3" className={classes.title}>
                                        {t('register.phoneTitle')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <Typography variant="body2">{t('register.phoneRequiredInfo')}</Typography>
                                </Grid>
                                <Grid item xs={12} className={classes.formItem}>
                                    <CustomTextField
                                        label={t('register.phone')}
                                        itemName={'phone'}
                                        password={false}
                                        children={<img alt="Card" src={IdCard} className={classes.inputIconLeft} />}
                                        errorChildren={<img alt="Card" src={IdCardRed} className={classes.inputIconLeft} />}
                                    ></CustomTextField>
                                </Grid>
                            </Grid>
                        </div>
                        <div className={classes.wrapperBottom}>
                            <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                                <Grid item xs={12} className={classes.buttonContainer}>
                                    <AsyncButton
                                        buttonProps={{
                                            className: classes.button,
                                            variant: 'contained',
                                            color: 'primary',
                                            type: 'submit',
                                            fullWidth: true,
                                            disabled: resendCountdown > 0,
                                        }}
                                        inProgress={inProgress}
                                    >
                                        <p className={classes.buttonText}>
                                            {resendCountdown > 0
                                                ? t('register.phoneResendIn', { seconds: resendCountdown })
                                                : t('register.phoneSendCodeButton')}
                                        </p>
                                    </AsyncButton>
                                </Grid>
                                {/* kod uz bol poslany - navrat na jeho zadanie */}
                                {sentToPhone && (
                                    <Grid item xs={12} className={classes.link}>
                                        <RouterLink
                                            to="#"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault()
                                                setCodeStep(true)
                                            }}
                                        >
                                            {t('register.phoneEnterReceivedCode')}
                                        </RouterLink>
                                    </Grid>
                                )}
                                {logoutLink}
                            </Grid>
                        </div>
                    </form>
                )}
            />
        )
    }

    return (
        <Form
            validate={(model: any): ValidationErrors => {
                const errors: any = {}
                setRequiredErrors(errors, model, ['code'], t('register.required'))
                return errors
            }}
            onSubmit={(model: any) => verifyCode(model.code)}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} noValidate className={classes.form} autoComplete="off">
                    <div className={classes.wrapper}>
                        <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                            <Grid item xs={12} className={classes.titleContainer}>
                                <Typography variant="h3" className={classes.title}>
                                    {t('register.phoneTitle')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.formItem}>
                                <Typography variant="body2">
                                    {t('register.phoneSentTo', { phone: sentToPhone })}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} className={classes.formItem}>
                                <CustomTextField
                                    label={t('register.phoneCodeLabel')}
                                    itemName={'code'}
                                    password={false}
                                    children={<img alt="Lock" src={PasswordIcon} className={classes.inputIconLeft} />}
                                    errorChildren={<img alt="Lock" src={PasswordRedIcon} className={classes.inputIconLeft} />}
                                ></CustomTextField>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.wrapperBottom}>
                        <Grid container alignItems="flex-start" className={classes.wrapperInner} spacing={0}>
                            <Grid item xs={12} className={classes.buttonContainer}>
                                <AsyncButton
                                    buttonProps={{
                                        className: classes.button,
                                        variant: 'contained',
                                        color: 'primary',
                                        type: 'submit',
                                        fullWidth: true,
                                    }}
                                    inProgress={inProgress}
                                >
                                    <p className={classes.buttonText}>{t('register.phoneVerifyButton')}</p>
                                </AsyncButton>
                            </Grid>
                            {/* rovnake tlacidlo ako Overit, pod nim */}
                            <Grid item xs={12} className={classes.secondButtonContainer}>
                                <AsyncButton
                                    buttonProps={{
                                        className: classes.button,
                                        variant: 'contained',
                                        color: 'primary',
                                        type: 'button',
                                        fullWidth: true,
                                        disabled: inProgress,
                                        onClick: () => setCodeStep(false),
                                    }}
                                    inProgress={false}
                                >
                                    <p className={classes.buttonText}>{t('register.phoneChangeNumber')}</p>
                                </AsyncButton>
                            </Grid>
                            <Grid item xs={12} className={classes.link}>
                                {resendCountdown > 0 ? (
                                    t('register.phoneResendIn', { seconds: resendCountdown })
                                ) : (
                                    <RouterLink
                                        to="#"
                                        onClick={(e: React.MouseEvent) => {
                                            e.preventDefault()
                                            sendCode(phone)
                                        }}
                                    >
                                        {t('register.phoneResendButton')}
                                    </RouterLink>
                                )}
                            </Grid>
                            {logoutLink}
                        </Grid>
                    </div>
                </form>
            )}
        />
    )
}

export default PhoneVerification
